/**
 * Contrato do formulário público de extensão de plano (RN 561).
 * Baseado em "Extension — Lane 5: Formulários (Público)" (2026-07-14), §5.
 *
 * Rota pública, autenticada só por token (sem login), no padrão `isExternal` já
 * usado em `src/pages/authentication/certificate-upload`:
 *
 *    /extensao/:token
 *
 * O token identifica o candidato; o CPF digitado no Step 0 é apenas confirmação
 * de identidade (spec §10.3). Token expirado → página 404 neutra, sem
 * diferenciar "nunca existiu" de "expirou" (spec §3).
 *
 * Endpoints (spec §5):
 *    GET    /extensao/:token/resolve    → IResolveResponse (não marca CONSUMED)
 *    POST   /extensao/:token/decline    → recusa no Step 0; token → CONSUMED
 *    POST   /extensao/:token/documents  → multipart; devolve a S3 key
 *    POST   /extensao/:token/submit     → ISubmitPayload; token → CONSUMED
 *
 * Sem salvamento parcial (spec §1): o estado do wizard vive em memória e um
 * refresh reinicia do Step 0.
 *
 * Convenções: datas ISO 8601 (`YYYY-MM-DD`); valores monetários em centavos
 * (inteiro), nunca float.
 */

/** A quem um campo ou documento se aplica. */
export type PersonType = "TITULAR" | "DEPENDENTE";

/**
 * Acomodação do plano. `QUARTO_PRIVATIVO_UTI` cobre o "Quarto Privativo + UTI"
 * que o Figma exibe no terceiro card do Step 2.
 */
export type Accommodation =
  | "ENFERMARIA"
  | "QUARTO_PRIVATIVO"
  | "QUARTO_PRIVATIVO_UTI";

/**
 * Forma de pagamento não é campo do contrato: a spec (Lane 5 §6) fixa boleto
 * bancário direto no Step 2 e no Step 5. Se um dia variar por contrato, aí sim
 * vira campo do `resolve`.
 */
export const PAYMENT_METHOD_LABEL = "Boleto bancário direto";

/** Limites de upload impostos pelo backend (spec §5), replicados no cliente. */
export const ACCEPTED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
] as const;

export const MAX_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024;

export const MAX_DOCUMENTS_TOTAL_SIZE_BYTES = 50 * 1024 * 1024;

/**
 * Campo habilitado pela configuração de Layout (`extension_layout_field`,
 * Lane 2). O front apenas renderiza o que vier habilitado.
 */
export interface ILayoutField {
  /** Identificador enviado no upload como `field_id` (ex.: "rg_frente"). */
  fieldId: string;
  /** Rótulo exibido (ex.: "RG (frente)"). */
  label: string;
  personType: PersonType;
  required: boolean;
}

/** Contexto da oferta, exibido no Step 0 antes de qualquer digitação. */
export interface IOfferContext {
  /** Usado em "Olá, [Nome]...". */
  beneficiaryName: string;
  /** Usado em "Seu benefício pela [Empresa] encerrou...". */
  companyName: string;
  /** "Você tem até o dia [Data de Expiração] para decidir...". */
  expirationDate: string;
  /**
   * Vigência ofertada em meses. Quem define o prazo é o backend — o front nunca
   * assume um valor fixo, porque o período varia conforme o caso (Lei 9.656/98,
   * arts. 30 e 31).
   */
  coverageMonths: number;
}

/** Titular — somente leitura, exceto o contato pessoal. */
export interface IHolder {
  name: string;
  /** Apenas dígitos; máscara aplicada na apresentação. */
  taxPayerId: string;
  birthDate: string;
}

/** Dependente atual da apólice, identificado por CPF (spec §5 submit). */
export interface IDependent {
  /** Chave de retenção enviada em `retained_dependent_cpfs`. */
  taxPayerId: string;
  name: string;
  /** Grau de parentesco formatado (ex.: "Cônjuge", "Filho"). */
  relationship: string;
  /** Exibida no card do Step 3. */
  age: number;
  birthDate?: string;
}

/** Campos que a edição inline do Step 3 permite corrigir. */
export interface IDependentEdit {
  name: string;
  relationship: string;
  taxPayerId: string;
  birthDate: string;
}

/**
 * Plano elegível. O Step 2 mostra os dois preços lado a lado, calculados sobre a
 * retenção de dependentes — que começa com todos mantidos e só é ajustada no
 * Step 3, seguinte (spec §7, §10.1).
 */
export interface IEligiblePlan {
  id: string;
  /** Ex.: "Amil Fácil S40". */
  name: string;
  operator: string;
  accommodation: Accommodation;
  /** Mensalidade só do titular, em centavos — usada na comparação. */
  holderOnlyPriceCents: number;
  /** Mensalidade adicional por dependente retido, em centavos. */
  perDependentPriceCents: number;
  /** Destacado como "Seu plano atual" no design. */
  isCurrentPlan: boolean;
}

/** Documento já enviado; a S3 key é o que vai no submit. */
export interface IUploadedDocument {
  /** Retornado por POST /documents e enviado em `uploaded_document_keys`. */
  s3Key: string;
  fieldId: string;
  personType: PersonType;
  /** CPF do titular ou do dependente a quem o documento pertence. */
  beneficiaryTaxPayerId: string;
  fileName: string;
  sizeBytes: number;
}

/** Resposta de GET /extensao/:token/resolve. */
export interface IResolveResponse {
  offer: IOfferContext;
  holder: IHolder;
  dependents: IDependent[];
  eligiblePlans: IEligiblePlan[];
  /** Campos de documento habilitados, para titular e dependentes. */
  layoutFields: ILayoutField[];
  /**
   * Verdadeiro quando já existe aceite LGPD para este candidato — o checkbox do
   * Step 0 volta pré-marcado (spec §10.6).
   */
  hasPriorLgpdAcceptance: boolean;
}

/** Body de POST /extensao/:token/decline. */
export interface IDeclinePayload {
  /** Aceite LGPD registrado junto com a recusa (spec §5). */
  lgpd_aceite: true;
}

/**
 * Body de POST /extensao/:token/submit, em snake_case exatamente como a spec §5
 * especifica.
 */
export interface ISubmitPayload {
  confirmed_email: string;
  /** Apenas dígitos, com DDD. */
  confirmed_phone: string;
  /** CPFs dos dependentes que o titular optou por manter. */
  retained_dependent_cpfs: string[];
  chosen_plan_id: string;
  /** S3 keys devolvidas por POST /documents. */
  uploaded_document_keys: string[];
  rn561_aceite: true;
}

/** Passos do wizard. O stepper só aparece a partir do Step 1 (spec §6). */
export enum ExtensionStep {
  Login = 0,
  Holder = 1,
  Plan = 2,
  Dependents = 3,
  Documents = 4,
  Review = 5,
}

/** Telas terminais do fluxo. */
export type ExtensionOutcome = "CONFIRMED" | "DECLINED";

/**
 * Escolha do Step 0. No design ("Sim, quero manter" / "Não, dispensar") ela é
 * uma seleção, confirmada depois pelo botão "Próximo" — não uma ação imediata.
 */
export type OfferDecision = "KEEP" | "DECLINE";

/**
 * Estado do wizard mantido em memória (sem salvamento parcial). `taxPayerId` é
 * o CPF de confirmação digitado no Step 0.
 */
export interface IExtensionFormState {
  taxPayerId: string;
  hasAcceptedLgpd: boolean;
  /**
   * Seleção do Step 0. Fica no estado do wizard, e não local na tela, para
   * sobreviver ao "Voltar" vindo do Step 1. Não vai no submit.
   */
  offerDecision: OfferDecision | null;
  contact: { personalEmail: string; mobilePhone: string };
  /**
   * CPFs dos dependentes mantidos; todos entram marcados por padrão.
   *
   * A chave é sempre o CPF que veio no `resolve`, nunca um CPF editado na tela:
   * é por ele que o backend identifica o dependente.
   */
  retainedDependentTaxPayerIds: string[];
  /**
   * Correções feitas na edição inline do Step 3, indexadas pelo CPF original do
   * `resolve`.
   *
   * PENDÊNCIA DE CONTRATO: `ISubmitPayload` não tem campo para estas correções —
   * hoje elas só alimentam a revisão do Step 5 e se perdem no envio. Enquanto o
   * backend não expuser onde recebê-las, a edição inline é apenas visual.
   */
  dependentEdits: Record<string, IDependentEdit>;
  chosenPlanId: string | null;
  uploadedDocuments: IUploadedDocument[];
  hasAcceptedRn561: boolean;
}
